<template>
  <div class="race-wrapper">
    <div
      v-show="!selectedImg.imgId"
      class="race-columns"
    >
      <Race
        v-for="column in visibleColumns"
        :key="column.id"
        :column="column"
        :filters="filters"
        :fullView="fullView"
        @selectImg="props => setImageViewer(props, column.id)"
        @updateGallery="getColumnImages"
        @updateSettings="updateUserSettings"
      />
      <ShowAsideButton
        class="show-compare-button"
        :class="{'is-open': showComparesFooter}"
        @toggle="changeComparePanelVisibility"
      />
      <CopmareRaceImageFooter
        v-if="showComparesFooter"
        class="compare-panel"
      />
    </div>
    <ImageViewer
      v-if="selectedImg.imgId"
      :paramImageId="selectedImg.imgId"
      @changeImage="changeImageViewer"
      @close="closeImageViewer"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { getRaceImagesFilters } from '@/api/raceimages'
import { getSettings, setSettings } from '@/api/settings'
import { DEFAULT_RACE_FILTER_OPTIONS } from '@/constants'
import CopmareRaceImageFooter from '@/components/raceImageComponents/CopmareRaceImageFooter'
import ImageViewer from '@/views/image/ImagePage'
import Race from './Race'
import ShowAsideButton from '@/components/ShowAsideButton'

const COLUMNS_COUNT_FOR_FULL_VIEW = 4
const DEFAULT_SELECTION = {
  columnId: null,
  imgId: null,
}

export default {
  name: 'RaceContainer',
  components: {
    CopmareRaceImageFooter,
    ImageViewer,
    Race,
    ShowAsideButton,
  },
  data() {
    return {
      filters: [],
      selectedImg: { ...DEFAULT_SELECTION },
    }
  },
  computed: {
    ...mapGetters({
      columns: 'race/columns',
      showComparesFooter: 'race/getIsShowComparesFooter'
    }),
    fullView() {
      return this.visibleColumns.length < COLUMNS_COUNT_FOR_FULL_VIEW
    },
    raceId() {
      return this.$route.params.raceId
    },
    visibleColumns() {
      return this.columns.filter(el => el?.filtersForUI?.showColumn)
    },
  },
  methods: {
    ...mapActions({
      changeComparePanelVisibility: 'race/changeIsShowComparesFooter',
      getImages: 'race/getImages',
      pollingImagesClear: 'race/pollingImagesClear',
      pollingImagesInit: 'race/pollingImagesInit',
      resetColumns: 'race/resetColumns',
      setColumnsFromStore: 'race/setColumnsFromStore',
      setCurrentRace: 'race/setCurrentRace',
      setInitialPollingList: 'race/setInitialPollingList',
      updateColumnsInStore: 'race/updateColumnsInStore',
    }),
    async changeImageViewer(direction) {
      const { imageList = [], page } = this.columns.find(el => el.id === this.selectedImg?.columnId) || {}
      const imageIndex = imageList.findIndex(({_id}) => _id === this.selectedImg?.imgId)

      if (imageIndex === undefined || (imageIndex === 0 && direction === 'prev')) {
        return
      }
      if (imageIndex < imageList.length - 1) {
        this.selectedImg.imgId = direction === 'next' ? imageList[imageIndex + 1]._id : imageList[imageIndex - 1]._id
        return
      }

      await this.getColumnImages({
        columnId: this.selectedImg?.columnId,
        page: page + 1,
      })
      this.changeImageViewer(direction)
    },
    closeImageViewer() {
      this.selectedImg = { ...DEFAULT_SELECTION }
    },
    getColumnImages(params) {
      return this.getImages({ ...params, raceId: this.raceId })
    },
    async getInitialImages() {
      try {
        const promises = this.visibleColumns.map(el => this.getColumnImages({
          columnId: el.id,
          direction: 'end',
          page: 1,
        }))
        await Promise.all(promises)
        const imgInfo = this.columns[0]?.imageList[0] || {}
        this.setCurrentRace({
          id: this.raceId,
          name: imgInfo.race_event,
        })
      } catch (e) {
        console.log(e);
      }
    },
    async getFilters() {
      try {
        const [{ data: filters }, settings] = await Promise.all([
          getRaceImagesFilters(this.raceId),
          getSettings(),
        ])
        const allFilters = [...filters.filter.header_filters, ...filters.filter.left_filters]
        const orderFilters = settings?.data?.raceFiltersOrder || {}

        this.filters = allFilters
          .reduce((newFilters, cur) => {
            const defaultField = DEFAULT_RACE_FILTER_OPTIONS[cur.filter_var]
            const resultFilter = defaultField.mergeProps ? defaultField.mergeProps(cur) : { ...cur, ...defaultField }
            const userOrder = orderFilters[cur.filter_var]
            if (userOrder) {
              resultFilter.order = userOrder
            }
            newFilters.push(resultFilter)
            return newFilters
          }, [])
          .sort((a, b) => a.order - b.order)
      } catch (e) {
        console.log(e);
      }
    },
    setImageViewer(imgId, columnId) {
      this.selectedImg = { columnId, imgId }
    },
    updateUserSettings({ newOrder, updatedFilters }) {
      this.filters = updatedFilters
      setSettings({ raceFiltersOrder: newOrder })
    },
    beforeunloadFunc() {
      this.updateColumnsInStore({
        columns: this.columns,
        raceId: this.raceId,
      })
      this.resetColumns()
    },
  },
  created() {
    this.setColumnsFromStore(this.raceId)
    this.setInitialPollingList()
    window.addEventListener('beforeunload', this.beforeunloadFunc)
  },
  mounted() {
    this.getFilters()
    this.getInitialImages()
    this.pollingImagesInit(this.raceId)
  },
  beforeDestroy() {
    window.removeEventListener('beforeunload', this.beforeunloadFunc)
    this.pollingImagesClear()
  },
  beforeRouteLeave(_, from, next) {
    this.updateColumnsInStore({
      columns: this.columns,
      raceId: from?.params?.raceId,
    })
    this.resetColumns()
    next();
  },
}
</script>

<style lang="scss" scoped>
.race-wrapper {
  width: 100%;
}

.race-columns {
  display: flex;
  position: relative;
  flex: 1;
}

.compare-panel {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1;
}

.show-compare-button {
  bottom: 15px;
  transform: rotate(90deg);

  &.is-open {
    bottom: 115px;
    transform: rotate(-90deg);
  }
}
</style>
